import client from './apiClient';
import {
  FeeConfigurationViewModel,
  FeeHudLineViewModel,
  FeeRulesVersionViewModel,
  CreateOrUpdateFeeConfigRequestViewModel,
  CreateOrUpdateFeeHudLineViewModel,
  PublishFeeRulesVersionRequestViewModel,
  FeeRuleViewModel,
  QuestionResponseDefaultViewModel,
  CreateOrUpdateQuestionResponseDefaultViewModel,
  FeesResponseViewModel,
  FeeResponseViewModel,
  FeeMigrationDiff,
  FeeMigrationImportRequest,
  FeeMigrationImportResult,
  EmulatorFeesRequestDto,
  FeeConfigGroupViewModel,
  CreateOrUpdateFeeConfigGroupViewModel,
} from '../models/fees';
import { SimpleBinaryExpression, SimpleExpression } from '../models/expression';

// ===== Sanitization Helpers =====

const stripRightValueFormat = (value: string | null | undefined): string => {
  if (!value) return '';
  return value
    .trim()
    .replace(/^["']/, '')
    .replace(/["']$/, '')
    .trim();
};

const sanitizeBinaryExpression = (expr: SimpleBinaryExpression): SimpleBinaryExpression => {
  return {
    ...expr,
    right: expr.right?.map(val => stripRightValueFormat(val)) || null,
  };
};

const sanitizeExpression = (expr: SimpleExpression | null | undefined): SimpleExpression | null => {
  if (!expr) return null;
  
  return {
    ...expr,
    expressions: expr.expressions?.map((item) => ({
      ...item,
      expression: item.expression ? sanitizeBinaryExpression(item.expression) : null,
      expressionGroup: item.expressionGroup ? sanitizeExpression(item.expressionGroup) : null,
    })) || null,
  };
};

const sanitizeRule = (rule: FeeRuleViewModel): FeeRuleViewModel => {
  return {
    ...rule,
    expression: sanitizeExpression(rule.expression),
  };
};

const sanitizeFeeConfiguration = (config: FeeConfigurationViewModel): FeeConfigurationViewModel => {
  return {
    ...config,
    rules: config.rules?.map(sanitizeRule) || null,
  };
};

const sanitizeFeeVersion = (version: FeeRulesVersionViewModel): FeeRulesVersionViewModel => {
  return {
    ...version,
    feeConfig: version.feeConfig?.map(sanitizeFeeConfiguration) || null,
  };
};

// ===== Fee Configurations =====

export const getAllFeeConfigurations = async (feeRulesVersionId?: number | null): Promise<FeeConfigurationViewModel[]> => {
  const { data } = await client.get<FeeConfigurationViewModel[]>('/fees/rules/fee-configurations', {
    params: feeRulesVersionId !== undefined && feeRulesVersionId !== null ? { feeRulesVersionId } : undefined,
  });
  return data.map(sanitizeFeeConfiguration);
};

export const getFeeConfiguration = async (id: number): Promise<FeeConfigurationViewModel> => {
  const { data } = await client.get<FeeConfigurationViewModel>(`/fees/rules/fee-configurations/${id}`);
  return sanitizeFeeConfiguration(data);
};

export const createFeeConfiguration = async (payload: CreateOrUpdateFeeConfigRequestViewModel): Promise<FeeConfigurationViewModel> => {
  const { data } = await client.post<FeeConfigurationViewModel>('/fees/rules/fee-configurations', payload);
  return sanitizeFeeConfiguration(data);
};

export const updateFeeConfiguration = async (id: number, payload: CreateOrUpdateFeeConfigRequestViewModel): Promise<FeeConfigurationViewModel> => {
  const { data } = await client.put<FeeConfigurationViewModel>(`/fees/rules/fee-configurations/${id}`, payload);
  return sanitizeFeeConfiguration(data);
};

export const deleteFeeConfiguration = async (id: number): Promise<void> => {
  await client.delete(`/fees/rules/fee-configurations/${id}`);
};

export const importFeeConfigurations = async (file: File): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await client.post('/fees/rules/fee-configurations/import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return data;
};

// ===== Fee Config Groups (DEV-3719) =====

export const getAllFeeConfigGroups = async (feeRulesVersionId?: number | null): Promise<FeeConfigGroupViewModel[]> => {
  const { data } = await client.get<FeeConfigGroupViewModel[]>('/fees/rules/fee-config-groups', {
    params: feeRulesVersionId !== undefined && feeRulesVersionId !== null ? { feeRulesVersionId } : undefined,
  });
  return data;
};

export const createFeeConfigGroup = async (payload: CreateOrUpdateFeeConfigGroupViewModel): Promise<FeeConfigGroupViewModel> => {
  const { data } = await client.post<FeeConfigGroupViewModel>('/fees/rules/fee-config-groups', payload);
  return data;
};

export const updateFeeConfigGroup = async (id: number, payload: CreateOrUpdateFeeConfigGroupViewModel): Promise<FeeConfigGroupViewModel> => {
  const { data } = await client.put<FeeConfigGroupViewModel>(`/fees/rules/fee-config-groups/${id}`, payload);
  return data;
};

export const deleteFeeConfigGroup = async (id: number): Promise<void> => {
  await client.delete(`/fees/rules/fee-config-groups/${id}`);
};

// ===== HUD Lines =====

export const getAllHudLines = async (feeRulesVersionId?: number | null): Promise<FeeHudLineViewModel[]> => {
  const { data } = await client.get<FeeHudLineViewModel[]>('/fees/hud-lines', {
    params: feeRulesVersionId !== undefined && feeRulesVersionId !== null ? { feeRulesVersionId } : undefined,
  });
  return data;
};

export const getHudLine = async (id: number): Promise<FeeHudLineViewModel> => {
  const { data } = await client.get<FeeHudLineViewModel>(`/fees/hud-lines/${id}`);
  return data;
};

export const createHudLine = async (payload: CreateOrUpdateFeeHudLineViewModel): Promise<FeeHudLineViewModel> => {
  const { data } = await client.post<FeeHudLineViewModel>('/fees/hud-lines', payload);
  return data;
};

export const updateHudLine = async (id: number, payload: CreateOrUpdateFeeHudLineViewModel): Promise<FeeHudLineViewModel> => {
  const { data } = await client.put<FeeHudLineViewModel>(`/fees/hud-lines/${id}`, payload);
  return data;
};

export const deleteHudLine = async (id: number): Promise<void> => {
  await client.delete(`/fees/hud-lines/${id}`);
};

export const importHudLines = async (file: File): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await client.post('/fees/hud-lines/import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return data;
};

// ===== Versions =====

export const getAllVersions = async (): Promise<FeeRulesVersionViewModel[]> => {
  const { data } = await client.get<FeeRulesVersionViewModel[]>('/fees/rules/versions');
  return data.map(sanitizeFeeVersion);
};

export const getVersion = async (id: number): Promise<FeeRulesVersionViewModel> => {
  const { data } = await client.get<FeeRulesVersionViewModel>(`/fees/rules/versions/${id}`);
  return sanitizeFeeVersion(data);
};

export const publishVersion = async (payload: PublishFeeRulesVersionRequestViewModel): Promise<FeeRulesVersionViewModel> => {
  const { data } = await client.post<FeeRulesVersionViewModel>('/fees/rules/versions/publish', payload);
  return sanitizeFeeVersion(data);
};

export const deleteVersion = async (id: number): Promise<void> => {
  await client.delete(`/fees/rules/versions/${id}`);
};

// ===== Fee Calculation =====

export const calculateSingleProductFees = async (payload: any): Promise<FeesResponseViewModel> => {
  const { data } = await client.post<FeesResponseViewModel>('/fees/single', payload);
  return data;
};

/**
 * Internal endpoint that runs single-product fee calculation pinned to a specific publication.
 * Used by the Fees Emulator to evaluate the same payload against multiple publications side-by-side.
 * The wrapper sets `settings.feeRulesVersionId` on the payload before posting.
 */
export const calculateSingleProductFeesForPublication = async (
  payload: any,
  feeRulesVersionId: number
): Promise<FeesResponseViewModel> => {
  // Strip useUnpublishedConfiguration: pinning to a specific version is incompatible with the
  // unpublished/draft mode, and the BE conflict guard returns 400 if both are set. The emulator
  // endpoint also ignores asOfDate when a version is pinned, but we leave that field alone (no
  // conflict, just unused).
  const { useUnpublishedConfiguration: _strip, ...restSettings } = payload?.settings ?? {};
  const emulatorPayload: EmulatorFeesRequestDto = {
    ...payload,
    settings: {
      ...restSettings,
      feeRulesVersionId,
    },
  };
  const { data } = await client.post<FeesResponseViewModel>('/fees/emulator/single', emulatorPayload);
  return data;
};

export const calculateMultipleProductFees = async (payload: any): Promise<FeeResponseViewModel[]> => {
  const { data } = await client.post<FeeResponseViewModel[]>('/fees', payload);
  return data;
};

// ===== Question Response Defaults =====

export const getAllQuestionResponseDefaults = async (): Promise<QuestionResponseDefaultViewModel[]> => {
  const { data } = await client.get<QuestionResponseDefaultViewModel[]>('/fees/question-response-defaults');
  return data;
};

export const getQuestionResponseDefault = async (id: number): Promise<QuestionResponseDefaultViewModel> => {
  const { data } = await client.get<QuestionResponseDefaultViewModel>(`/fees/question-response-defaults/${id}`);
  return data;
};

export const createQuestionResponseDefault = async (
  payload: CreateOrUpdateQuestionResponseDefaultViewModel
): Promise<QuestionResponseDefaultViewModel> => {
  const { data } = await client.post<QuestionResponseDefaultViewModel>('/fees/question-response-defaults', payload);
  return data;
};

export const updateQuestionResponseDefault = async (
  id: number,
  payload: CreateOrUpdateQuestionResponseDefaultViewModel
): Promise<QuestionResponseDefaultViewModel> => {
  const { data } = await client.put<QuestionResponseDefaultViewModel>(
    `/fees/question-response-defaults/${id}`,
    payload
  );
  return data;
};

export const deleteQuestionResponseDefault = async (id: number): Promise<void> => {
  await client.delete(`/fees/question-response-defaults/${id}`);
};

// ===== Migration =====

export const exportMigration = async (feeConfigurationGuid?: string): Promise<Blob> => {
  const { data } = await client.get('/fees/rules/versions/migration/export', {
    params: feeConfigurationGuid ? { feeConfigurationGuid } : undefined,
    responseType: 'blob',
  });
  return data;
};

export const prepareImportMigration = async (file: File): Promise<FeeMigrationDiff> => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await client.post<FeeMigrationDiff>('/fees/rules/versions/migration/prepare', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const importMigration = async (request: FeeMigrationImportRequest): Promise<FeeMigrationImportResult> => {
  const { data } = await client.post<FeeMigrationImportResult>('/fees/rules/versions/migration/import', request);
  return data;
};

